import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { TrendingUp, AlertCircle, CheckCircle2 } from "lucide-react";

const pct = (v) => `${(v * 100).toFixed(2)}%`;

export default function AdminRateModel() {
  const { user } = useAuth();
  const [model, setModel] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [draft, setDraft] = useState({ xgate_cost_rate: "", eur_usd_buffer: "", margin: "", network_fee_eur: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get("/admin/rate-model").then((r) => {
      setModel(r.data);
      setDraft({
        xgate_cost_rate: String(r.data.xgate_cost_rate),
        eur_usd_buffer:  String(r.data.eur_usd_buffer),
        margin:          String(r.data.margin),
        network_fee_eur: String(r.data.network_fee_eur),
      });
    });
    api.get("/admin/rate-model/proposals").then((r) => setProposals(r.data.proposals || []));
  };
  useEffect(() => { load(); }, []);

  if (!model) return <AdminLayout title="Rate model"><div className="text-sm text-muted-foreground">Loading…</div></AdminLayout>;

  // Live preview as operator types
  const d = {
    xgate_cost_rate: parseFloat(draft.xgate_cost_rate || model.xgate_cost_rate) || 0,
    eur_usd_buffer:  parseFloat(draft.eur_usd_buffer  || model.eur_usd_buffer)  || 0,
    margin:          parseFloat(draft.margin          || model.margin)          || 0,
    network_fee_eur: parseFloat(draft.network_fee_eur || model.network_fee_eur) || 0,
  };
  const allIn = d.xgate_cost_rate + d.eur_usd_buffer + d.margin;
  const netPer100 = 100 - 100 * d.xgate_cost_rate - d.network_fee_eur;
  const changed = (
    d.xgate_cost_rate !== model.xgate_cost_rate ||
    d.eur_usd_buffer  !== model.eur_usd_buffer  ||
    d.margin          !== model.margin          ||
    d.network_fee_eur !== model.network_fee_eur
  );

  const propose = async () => {
    setSubmitting(true);
    try {
      const body = {};
      if (d.xgate_cost_rate !== model.xgate_cost_rate) body.xgate_cost_rate = d.xgate_cost_rate;
      if (d.eur_usd_buffer  !== model.eur_usd_buffer)  body.eur_usd_buffer  = d.eur_usd_buffer;
      if (d.margin          !== model.margin)          body.margin          = d.margin;
      if (d.network_fee_eur !== model.network_fee_eur) body.network_fee_eur = d.network_fee_eur;
      const r = await api.patch("/admin/rate-model", body);
      toast.success(`Proposal ${r.data.proposal_id.slice(0, 8)}… waiting on a second admin to approve.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Propose failed");
    } finally { setSubmitting(false); }
  };

  const approve = async (id) => {
    try {
      await api.post(`/admin/rate-model/approve/${id}`);
      toast.success("Approved — rate model updated.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  return (
    <AdminLayout title="Rate model"
                 subtitle="Three separately editable fields. Customer all-in is derived. Maker-checker required.">
      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        {/* Editor */}
        <div className="xch-card p-5 space-y-4" data-testid="rate-editor">
          <Field label="XGate cost rate (decimal)" hint="Current XGate tier rate. 0.0450 = 4.50%.">
            <Input type="number" step="0.0001" min="0" max="0.20" value={draft.xgate_cost_rate}
                   onChange={(e) => setDraft({ ...draft, xgate_cost_rate: e.target.value })}
                   className="rounded-xl font-mono" data-testid="rate-cost-input" />
          </Field>
          <Field label="EUR/USD buffer (decimal)" hint="Buffer covering the EUR/USD position carried between deposit and T+1 settlement. Not optional.">
            <Input type="number" step="0.0001" min="0" max="0.10" value={draft.eur_usd_buffer}
                   onChange={(e) => setDraft({ ...draft, eur_usd_buffer: e.target.value })}
                   className="rounded-xl font-mono" data-testid="rate-buffer-input" />
          </Field>
          <Field label="Margin (decimal)" hint="Our profit markup. Independent of cost.">
            <Input type="number" step="0.0001" min="0" max="0.10" value={draft.margin}
                   onChange={(e) => setDraft({ ...draft, margin: e.target.value })}
                   className="rounded-xl font-mono" data-testid="rate-margin-input" />
          </Field>
          <Field label="Network fee floor (€)" hint="Fixed on-chain fee added per order.">
            <Input type="number" step="0.01" min="0" max="20" value={draft.network_fee_eur}
                   onChange={(e) => setDraft({ ...draft, network_fee_eur: e.target.value })}
                   className="rounded-xl font-mono" data-testid="rate-network-input" />
          </Field>
          <Button onClick={propose} disabled={!changed || submitting}
                  className="bg-primary hover:bg-primary/90 text-white rounded-xl font-display font-semibold w-full"
                  data-testid="rate-propose-btn">
            {submitting ? "Submitting…" : "Submit for approval"}
          </Button>
          {changed && (
            <div className="text-[11px] text-muted-foreground flex items-start gap-1.5">
              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-600" />
              A second admin must approve. You cannot approve your own proposal.
            </div>
          )}
        </div>

        {/* Live preview */}
        <div className="xch-card p-5 space-y-3" data-testid="rate-preview">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <div className="font-display text-base font-bold text-foreground">Live preview</div>
          </div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Customer all-in rate</div>
          <div className="font-display text-4xl font-extrabold text-primary tabular-nums" data-testid="preview-all-in">
            {pct(allIn)}
          </div>
          <div className="border-t border-border pt-3 space-y-1.5 text-sm">
            <PreviewRow label="XGate cost" value={pct(d.xgate_cost_rate)} />
            <PreviewRow label="EUR/USD buffer" value={pct(d.eur_usd_buffer)} />
            <PreviewRow label="Margin" value={pct(d.margin)} bold />
            <PreviewRow label="Network fee" value={`€${d.network_fee_eur.toFixed(2)}`} />
          </div>
          <div className="border-t border-border pt-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Net to us per €100</div>
            <div className="font-mono text-lg font-bold text-foreground tabular-nums" data-testid="preview-net">
              €{netPer100.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {proposals.length > 0 && (
        <div className="xch-card p-5 mt-5 space-y-3" data-testid="rate-proposals">
          <div className="font-display text-base font-bold text-foreground">Pending proposals</div>
          {proposals.map((p) => {
            const isOwn = user && p.proposer_email === user.email;
            return (
            <div key={p.id} className="border border-border rounded-xl p-3 flex items-center gap-3"
                 data-testid={`proposal-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground">{p.proposer_email}{isOwn && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">your proposal</span>}</div>
                <div className="text-[11px] text-muted-foreground">{new Date(p.created_at).toLocaleString("en-IE")}</div>
                <div className="text-xs mt-1 font-mono">
                  {Object.entries(p.proposed_changes).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                </div>
              </div>
              <Button onClick={() => approve(p.id)} size="sm" disabled={isOwn}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg gap-1 disabled:bg-muted disabled:text-muted-foreground"
                      title={isOwn ? "Maker-checker: a different admin must approve" : "Approve"}
                      data-testid={`approve-proposal-${p.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5" /> {isOwn ? "Awaits another admin" : "Approve"}
              </Button>
            </div>
          );})}
        </div>
      )}
    </AdminLayout>
  );
}

const Field = ({ label, hint, children }) => (
  <div className="space-y-1">
    <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10px] text-muted-foreground leading-relaxed">{hint}</p>}
  </div>
);

const PreviewRow = ({ label, value, bold }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-mono tabular-nums ${bold ? "font-bold text-foreground" : ""}`}>{value}</span>
  </div>
);
